import React, { useEffect } from 'react';
import Link, { LinkProps } from '@soluto-design/link';
import Picture from '@soluto-design/picture';
import { Button, Icon, Text, useIsBreakpointUp } from '@soluto-design/react';
import cls from '@soluto-design/styles/cls';

import useDisableScroll from '../../../../../hooks/useDisableScroll';

import { useHeaderContext } from '../..';

interface MegaMenuLinkProps extends LinkProps {
    items?: LinkProps[];
}

export interface MegaMenuProps {
    menu?: MegaMenuLinkProps[];
    buttons: LinkProps[];
    highlightedPost: {
        title: string;
        summary: string;
        media: Record<string, unknown>;
        cta: LinkProps;
        onClick: (ev: React.MouseEvent) => void;
    };
    highlightedPostTitle: LinkProps;
    video: any;
    banner?: string;
}

function ChildLink({
    link,
    className,
}: {
    link: LinkProps;
    className?: string;
}) {
    return (
        <Link
            href={link?.href}
            target={link?.target}
            classes={{
                root: cls(
                    'block text-c100 !font-regular !text-text-secondary hover:!text-text-primary !no-underline',
                    className,
                ),
                active: '!text-text-primary',
            }}>
            {link?.label ?? link?.children}
        </Link>
    );
}

const DesktopLink = ({
    link,
    className,
}: {
    link: MegaMenuLinkProps;
    className?: string;
}) => {
    return (
        <Link
            href={link.href}
            target={link.target}
            title={link.title}
            partiallyActive
            classes={{
                root: cls(
                    'flex justify-between lg:justify-start items-center text-b400 text-text-primary !font-semibold !no-underline gap-x-8',
                    className,
                ),
                active: '!text-text-brand-1',
            }}>
            <span>{link?.label ?? link?.children} </span>
            <Icon icon="arrow-direction--type-right" className="!h-24 !w-24" />
        </Link>
    );
};

function ParentLink({
    link,
    onClick,
    isOpen,
    index,
    columnIndex,
}: {
    link: MegaMenuLinkProps;
    onClick: (columnIndex: number, index: number) => void;
    isOpen: boolean;
    index: number;
    columnIndex: number;
}) {
    if (!link?.items?.length) {
        return <DesktopLink link={link} className={'lg:pb-16'} />;
    }

    return (
        <>
            {/* Desktop */}
            <div className="hidden lg:flex lg:flex-col lg:gap-16 lg:pb-xxl">
                <DesktopLink link={link} />
                {link.items && link.items.length > 0 && (
                    <ul className="flex flex-col gap-16">
                        {link.items?.map((subLink, i) => (
                            <li key={`subLink-${i}`}>
                                <ChildLink link={subLink} />
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            {/* Mobile */}
            <div
                className="flex flex-col lg:hidden "
                role="button"
                onClick={() => onClick(columnIndex, index)}>
                <Text
                    component="div"
                    className="inline-flex justify-between items-center text-b400 !text-text-primary !font-semibold !no-underline">
                    <span>{link?.label ?? link?.children} </span>
                    <Icon
                        icon={!isOpen ? 'plus' : 'minus'}
                        className="!h-24 !w-24"
                    />
                </Text>

                {link.items && link.items.length > 0 && (
                    <div
                        className={cls(
                            'transition-all duration-300 ease-in-out grid grid-rows-[0fr]',
                            isOpen && 'grid-rows-[1fr] overflow-auto',
                        )}>
                        <div className="overflow-hidden">
                            <ul className="flex flex-col gap-y-32 mt-32">
                                {[link, ...link?.items].map((subLink, i) => (
                                    <li key={`subLink-${i}`} className="pl-16 ">
                                        <ChildLink link={subLink} />
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

export default function MegaMenu({
    menu = [],
    buttons = [],
    highlightedPostTitle,
    highlightedPost,
    banner,
}: MegaMenuProps) {
    const { toggleColumn, columnsOpen, isOpen } = useHeaderContext();

    const isDesktop = useIsBreakpointUp('lg');

    const [blockScroll, allowScroll] = useDisableScroll();

    useEffect(() => {
        if (isDesktop) {
            allowScroll();
            return;
        }

        if (isOpen) {
            blockScroll();
            return;
        }

        allowScroll();

        return () => {
            allowScroll();
        };
    }, [allowScroll, blockScroll, isOpen, isDesktop]);

    return (
        <nav className="flex flex-col max-h-full">
            <div
                className={cls(
                    'row flex-initial overflow-y-auto scrollbar-hide lg:py-xl',
                )}>
                <div className="col-span-full lg:col-span-9 xl:col-span-9 max-h-full">
                    {menu && menu.length > 0 && (
                        <nav className="grid lg:grid-cols-9 lg:gap-x-gutter">
                            {menu?.map(({ items }, i) => (
                                <div
                                    key={`column-${i}`}
                                    className="flex flex-col gap-y-32 pb-16 pt-16 col-span-full lg:col-span-3 lg:gap-0 lg:py-0">
                                    {items &&
                                        items?.length > 0 &&
                                        items.map((link, linkIndex) => {
                                            const isOpen =
                                                columnsOpen?.[i]?.includes(
                                                    linkIndex,
                                                );

                                            return (
                                                <ParentLink
                                                    key={`column-${i}-${linkIndex}`}
                                                    link={link}
                                                    columnIndex={i}
                                                    index={linkIndex}
                                                    isOpen={isOpen}
                                                    onClick={toggleColumn}
                                                />
                                            );
                                        })}
                                </div>
                            ))}
                        </nav>
                    )}
                </div>
                {highlightedPost && highlightedPostTitle && (
                    <div className="hidden xl:block xl:col-span-3">
                        <Link
                            href={highlightedPostTitle.href}
                            className="pb-[30px] block !no-underline">
                            {highlightedPostTitle.label}
                        </Link>
                        <div
                            className="cursor-pointer hover:opacity-80 transition-opacity duration-300 ease-in-out"
                            role="link"
                            onClick={highlightedPost?.onClick}>
                            <Picture
                                classes={{
                                    wrapper: 'rounded-medium overflow-hidden',
                                }}
                                layout="responsive"
                                {...highlightedPost?.media}
                            />

                            <Text
                                component="p"
                                className="text-text-primary text-b300 mt-[30px]">
                                {highlightedPost?.title}
                            </Text>

                            <Text
                                component="div"
                                isHtml
                                className="text-text-secondary text-c100 mt-16">
                                {highlightedPost?.summary}
                            </Text>
                        </div>
                        <Button
                            href={highlightedPost?.cta?.href}
                            onClick={highlightedPost?.cta?.onClick}
                            variant="secondary"
                            size="large"
                            className="mt-[30px] [&>svg]:h-24"
                            rightIcon={'arrow-direction--type-right'}>
                            {highlightedPost?.cta?.label}
                        </Button>
                    </div>
                )}
            </div>

            {buttons?.length > 0 && (
                <div className="row py-xg lg:hidden flex-1">
                    {buttons?.map((link, index) => (
                        <div
                            className="col-span-2 md:col-span-4"
                            key={`button-bottom-${link?.label}-${index}`}>
                            <Button
                                href={link?.href}
                                onClick={link?.onClick}
                                variant={link?.variant ?? 'primary'}
                                size="large"
                                className="w-full">
                                {link?.label ?? link?.children}
                            </Button>
                        </div>
                    ))}
                </div>
            )}

            {banner && (
                <Text
                    component="div"
                    isHtml
                    className="text-b400 text-text-primary [&>p>a]:!text-text-brand-1 text-center py-md lg:py-xs">
                    {banner}
                </Text>
            )}
        </nav>
    );
}
